import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, cn } from "@exhousing/design-system";
import {
  Check,
  Clock,
  ExternalLink,
  FileSignature,
  FileText,
  Paperclip,
  PenLine,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/StatusBadge";
import {
  type Requirement,
  openRequirementFile,
  useCreateRequirement,
  useDeleteRequirement,
  useRequirements,
  useSendForSigning,
  useUpdateRequirement,
  useUploadRequirementFile,
} from "@/hooks/api/useRequirements";

const PARTY_LABEL: Record<string, string> = {
  tenant: "Tenant provides",
  landlord: "Landlord provides",
  agency: "You provide",
};

// The gates, in the order the lifecycle needs them. `other` catches ungated extras.
const GATES = [
  { key: "activate", title: "To activate the tenancy" },
  { key: "close", title: "To close the tenancy" },
  { key: "other", title: "Other documents" },
] as const;

function isSatisfied(r: Requirement) {
  return r.status === "accepted";
}

export function ContractRequirements({ contractId }: { contractId: string }) {
  const { data: reqs, isLoading } = useRequirements(contractId);
  const [adding, setAdding] = useState(false);

  if (isLoading) return null;
  const all = reqs ?? [];

  const grouped = GATES.map((g) => ({
    ...g,
    items: all.filter((r) => (r.gate ?? "other") === g.key),
  })).filter((g) => g.items.length > 0 || g.key === "activate");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-prose text-sm text-muted-foreground">
          The documents and signatures this tenancy needs. Request them, record what comes
          back, then accept or reject. This guides the lifecycle — it never blocks it.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => setAdding((v) => !v)}
        >
          <Plus className="size-4" />
          Add requirement
        </Button>
      </div>

      {adding && <AddRequirementForm contractId={contractId} onDone={() => setAdding(false)} />}

      {grouped.map((g) => {
        const ready = g.items.filter(isSatisfied).length;
        return (
          <Card key={g.key}>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">{g.title}</CardTitle>
              {g.items.length > 0 && (
                <span
                  className={cn(
                    "text-sm font-medium",
                    ready === g.items.length ? "text-success" : "text-muted-foreground",
                  )}
                >
                  {ready} of {g.items.length} ready
                </span>
              )}
            </CardHeader>
            <CardContent className="divide-y divide-border p-0">
              {g.items.length === 0 ? (
                <p className="px-6 py-4 text-sm text-muted-foreground">
                  Nothing required here yet.
                </p>
              ) : (
                g.items.map((r) => (
                  <RequirementRow key={r.id} contractId={contractId} req={r} />
                ))
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function RequirementRow({ contractId, req }: { contractId: string; req: Requirement }) {
  const update = useUpdateRequirement(contractId);
  const upload = useUploadRequirementFile(contractId);
  const remove = useDeleteRequirement(contractId);
  const sendSign = useSendForSigning(contractId);
  const fileRef = useRef<HTMLInputElement>(null);

  const [signing, setSigning] = useState(false);
  const [signer, setSigner] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmResend, setConfirmResend] = useState(false);

  const satisfied = isSatisfied(req);
  const busy =
    update.isPending || upload.isPending || remove.isPending || sendSign.isPending;
  // Out for e-signature: any non-terminal signing state (sent/opened/viewed/signed)
  // keeps the row in the "awaiting" view, so an intermediate event never exposes the
  // manual Accept/Reject for a document that hasn't come back.
  const awaitingSign =
    !!req.signing_status &&
    req.signing_status !== "completed" &&
    req.signing_status !== "rejected" &&
    !satisfied;

  const send = (resend = false) =>
    sendSign.mutate(req.id, {
      onSuccess: () => toast.success(resend ? "Reminder sent" : "Sent for signing"),
      onError: (e) => toast.error(e.message),
    });

  const patch = (body: Parameters<typeof update.mutate>[0]["body"]) =>
    update.mutate(
      { reqId: req.id, body },
      { onError: (e) => toast.error(e.message) },
    );

  const onUpload = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(
      { reqId: req.id, file },
      { onError: (e) => toast.error(e.message) },
    );
  };

  const submitSignature = () => {
    if (!signer.trim()) return;
    patch({ signed_by: signer.trim() });
    setSigning(false);
    setSigner("");
  };

  const submitRejection = () => {
    patch({ status: "rejected", reject_reason: reason.trim() || undefined });
    setRejecting(false);
    setReason("");
  };

  return (
    <div className={cn("px-6 py-4", satisfied && "opacity-70")}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-full",
              satisfied ? "bg-success/15 text-success" : "bg-muted text-muted-foreground",
            )}
          >
            {req.mode === "sign" ? (
              <FileSignature className="size-4" />
            ) : (
              <FileText className="size-4" />
            )}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium">{req.subject}</p>
            <p className="text-xs text-muted-foreground">
              {PARTY_LABEL[req.party] ?? req.party}
              {req.mode === "sign" ? " · signature" : " · document"}
              {req.signed_by && ` · signed by ${req.signed_by}`}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {awaitingSign ? (
            <Badge tone="warning" dot>
              Out for signature
            </Badge>
          ) : (
            <StatusBadge status={req.status} />
          )}
          {!req.is_standard && (
            <button
              type="button"
              aria-label="Remove requirement"
              disabled={busy}
              onClick={() => remove.mutate(req.id, { onError: (e) => toast.error(e.message) })}
              className="text-muted-foreground transition hover:text-danger"
            >
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
      </div>

      {req.status === "rejected" && req.reject_reason && (
        <p className="mt-2 flex items-center gap-1.5 pl-10 text-xs text-danger">
          <X className="size-3.5" /> {req.reject_reason}
        </p>
      )}

      {/* Inline editors */}
      {signing && (
        <InlineForm
          placeholder="Who signed? e.g. Eva Tenant"
          value={signer}
          onChange={setSigner}
          onSubmit={submitSignature}
          onCancel={() => setSigning(false)}
          submitLabel="Record signature"
        />
      )}
      {rejecting && (
        <InlineForm
          placeholder="Reason (optional) — e.g. expired, unreadable"
          value={reason}
          onChange={setReason}
          onSubmit={submitRejection}
          onCancel={() => setRejecting(false)}
          submitLabel="Reject"
          danger
        />
      )}

      {/* Actions */}
      {!signing && !rejecting && (
        <div className="mt-3 flex flex-wrap items-center gap-2 pl-10">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
          {req.file_name && (
            <Badge
              tone="neutral"
              className="cursor-pointer"
              onClick={() => openRequirementFile(contractId, req.id).catch(() => toast.error("Couldn't open file"))}
            >
              <Paperclip className="mr-1 size-3" />
              {req.file_name}
            </Badge>
          )}

          {awaitingSign ? (
            <>
              <Badge tone="info">
                <Clock className="mr-1 size-3" />
                Awaiting signature
              </Badge>
              {req.signing_url && (
                <a
                  href={req.signing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Open signing page
                  <ExternalLink className="size-3.5" />
                </a>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirmResend(true)}
              >
                <RefreshCw className="size-4" />
                Resend
              </Button>
            </>
          ) : req.status === "submitted" ? (
            <>
              <Button size="sm" disabled={busy} onClick={() => patch({ status: "accepted" })}>
                <Check className="size-4" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-danger hover:text-danger"
                disabled={busy}
                onClick={() => setRejecting(true)}
              >
                Reject
              </Button>
            </>
          ) : satisfied ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => patch({ status: "requested" })}
            >
              Reopen
            </Button>
          ) : (
            <>
              {req.mode === "sign" && (
                <Button size="sm" disabled={busy} onClick={() => send()}>
                  <PenLine className="size-4" />
                  Send for signing
                </Button>
              )}
              {req.mode === "sign" && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setSigning(true)}>
                  <FileSignature className="size-4" />
                  Mark signed
                </Button>
              )}
              <Button
                size="sm"
                variant={req.mode === "sign" ? "ghost" : "primary"}
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <Paperclip className="size-4" />
                {req.file_name ? "Replace file" : "Upload"}
              </Button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmResend}
        title="Resend the signing link?"
        message="A new link is emailed to the tenant and the previous link stops working."
        confirmLabel="Resend"
        onConfirm={() => {
          send(true);
          setConfirmResend(false);
        }}
        onCancel={() => setConfirmResend(false)}
      />
    </div>
  );
}

function InlineForm({
  placeholder,
  value,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  danger,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  danger?: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 pl-10">
      <Input
        autoFocus
        className="h-9 max-w-xs"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
          if (e.key === "Escape") onCancel();
        }}
      />
      <Button size="sm" variant={danger ? "destructive" : "primary"} onClick={onSubmit}>
        {submitLabel}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

const PARTY_OPTIONS = [
  { value: "tenant", label: "Tenant" },
  { value: "landlord", label: "Landlord" },
  { value: "agency", label: "Agency (you)" },
];
const MODE_OPTIONS = [
  { value: "provide", label: "Document" },
  { value: "sign", label: "Signature" },
];
const GATE_OPTIONS = [
  { value: "activate", label: "To activate" },
  { value: "close", label: "To close" },
  { value: "", label: "No gate" },
];

function AddRequirementForm({
  contractId,
  onDone,
}: {
  contractId: string;
  onDone: () => void;
}) {
  const create = useCreateRequirement(contractId);
  const [subject, setSubject] = useState("");
  const [party, setParty] = useState("tenant");
  const [mode, setMode] = useState("provide");
  const [gate, setGate] = useState("activate");

  const submit = () => {
    if (!subject.trim()) return;
    create.mutate(
      {
        subject: subject.trim(),
        party,
        mode,
        gate: gate || null,
      },
      {
        onSuccess: () => {
          toast.success("Requirement added");
          onDone();
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const selectCls =
    "h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <Card>
      <CardContent className="flex flex-wrap items-end gap-3 py-4">
        <div className="min-w-[14rem] flex-1">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            What's needed
          </label>
          <Input
            autoFocus
            placeholder="e.g. Employer statement"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <Selector label="From" value={party} onChange={setParty} options={PARTY_OPTIONS} cls={selectCls} />
        <Selector label="Kind" value={mode} onChange={setMode} options={MODE_OPTIONS} cls={selectCls} />
        <Selector label="Gate" value={gate} onChange={setGate} options={GATE_OPTIONS} cls={selectCls} />
        <Button disabled={create.isPending || !subject.trim()} onClick={submit}>
          Add
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </CardContent>
    </Card>
  );
}

function Selector({
  label,
  value,
  onChange,
  options,
  cls,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  cls: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      <select className={cls} value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
